"use client";

import {
  Box,
  Button,
  Heading,
  HStack,
  Separator,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useCallback, useMemo } from "react";
import { GetThoughtsRequest } from "@/generated/fastapi";
import { useUserChoices } from "@/state/globalStore";
import {
  THOUGHT_QUERY_DEFAULTS,
  useResetReflectionsMutation,
  useThoughtRefresh,
  useThoughtsQuery,
} from "@/features/thoughts";
import { getDimensionScore } from "@/features/reflect";
import { showErrorToast, showSuccessToast } from "@/shared/feedback";
import GenericDrawerInfo from "@/shared/ui/display/GenericDrawerInfo";
import GenericEmptyComponent from "@/shared/ui/GenericEmptyComponent";
import GenericIcon from "@/shared/ui/actions/GenericIcon";
import GenericRadarChart from "@/shared/ui/charts/GenericRadarChart";
import LoadingState from "@/shared/ui/LoadingState";
import { ReflectQuestionnaireContent } from "./ReflectQuestionnaire";
import ReflectQuestionnaireControls from "./ReflectQuestionnaireControls";

export type ReflectSingleThoughtProps = {
  thought: { objectID: string };
};

export default function ReflectSingleThought({
  thought,
}: ReflectSingleThoughtProps) {
  const thoughtId = thought.objectID;
  const resetReflectionsMutation = useResetReflectionsMutation();
  const { refreshReflectionSurfaces } = useThoughtRefresh();
  const { questionnaireViewModes } = useUserChoices();
  const viewMode = questionnaireViewModes[0] ?? "thought-oriented";
  const request = useMemo<GetThoughtsRequest>(
    () => ({
      selected_ids: [thoughtId],
      situation_scope: THOUGHT_QUERY_DEFAULTS.singleThoughtLookupSituationScope,
    }),
    [thoughtId],
  );
  const { data, isLoading, isError } = useThoughtsQuery(request, {
    enabled: Boolean(thoughtId),
  });
  const thoughtToReflect = data?.[0];
  const assessmentScore = thoughtToReflect
    ? {
        d1: getDimensionScore(thoughtToReflect, "dim0"),
        d2: getDimensionScore(thoughtToReflect, "dim1"),
        d3: getDimensionScore(thoughtToReflect, "dim2"),
        d4: getDimensionScore(thoughtToReflect, "dim3"),
      }
    : undefined;
  const reflectedDimensionCount = thoughtToReflect
    ? [
        thoughtToReflect.dim0,
        thoughtToReflect.dim1,
        thoughtToReflect.dim2,
        thoughtToReflect.dim3,
      ].filter(
        (dimension) =>
          dimension?.score !== null && dimension?.score !== undefined,
      ).length
    : 0;
  const hasReflections = reflectedDimensionCount > 0;
  const hasRemainingPrompts = reflectedDimensionCount < 4;
  const isResetting = resetReflectionsMutation.isPending;
  const reflectionStatusItem =
    reflectedDimensionCount === 0
      ? { label: "No reflections yet", colorPalette: "gray" as const }
      : reflectedDimensionCount === 4
        ? { label: "Fully reflected", colorPalette: "green" as const }
        : {
            label:
              reflectedDimensionCount === 1
                ? "1 reflection provided"
                : `${reflectedDimensionCount} reflections provided`,
            colorPalette: "purple" as const,
          };

  const handleResetReflections = useCallback(async () => {
    if (!hasReflections || isResetting) {
      return;
    }

    try {
      await resetReflectionsMutation.mutateAsync({
        thoughts_id: [thoughtId],
      });
      refreshReflectionSurfaces();
      showSuccessToast("Reflections reset");
    } catch {
      showErrorToast("Failed to reset reflections");
    }
  }, [
    hasReflections,
    isResetting,
    refreshReflectionSurfaces,
    resetReflectionsMutation,
    thoughtId,
  ]);

  if (isLoading) {
    return <LoadingState message="Loading thought to reflect..." />;
  }

  if (!thoughtToReflect) {
    return (
      <GenericEmptyComponent
        title={isError ? "Could not load thought" : "No thought found"}
        message={
          isError
            ? "Try again in a moment."
            : "The thought you are trying to reflect could not be found."
        }
      />
    );
  }

  if (!thoughtToReflect.situation_id) {
    return (
      <GenericEmptyComponent
        title="Anchor thought first"
        message="This thought is not assigned to a situation yet. Anchor it to a situation before adding reflections."
      />
    );
  }

  return (
    <VStack align="stretch" gap="4">
      <GenericDrawerInfo
        description="Reflect this thought so the profile understands its strength and shape."
        items={[reflectionStatusItem]}
      />

      <Separator />

      <HStack justify="space-between" align="start" gap="4">
        <Stack gap="1" align="stretch" minW={0}>
          <Heading size="sm">Reflect thought</Heading>
          <Text fontSize="sm" color="moveon.text.muted">
            {hasRemainingPrompts
              ? "Answer the remaining prompts for this thought."
              : "Review the reflection scores for this thought."}
          </Text>
        </Stack>
      </HStack>

      <ReflectQuestionnaireControls />

      <Stack gap="3" align="stretch">
        <HStack justify="space-between" align="start" gap="4">
          <Stack gap="1" align="stretch" minW={0} flex="1">
            <Heading size="sm">Reflection scores</Heading>
          </Stack>

          {hasReflections ? (
            <Button
              type="button"
              variant="outline"
              color="moveon.warning"
              borderColor="moveon.warningBorder"
              disabled={isResetting}
              onClick={() => {
                void handleResetReflections();
              }}
              aria-label="Reset reflections"
              minW={{ base: "10", sm: "auto" }}
            >
              <GenericIcon icon="reset" loading={isResetting} />
              <Text as="span" display={{ base: "none", sm: "inline" }}>
                Reset
              </Text>
            </Button>
          ) : null}
        </HStack>
        <Box display="flex" justifyContent="center" alignItems="center">
          <GenericRadarChart
            assessmentScore={assessmentScore}
            chartColor="purple.solid"
          />
        </Box>
      </Stack>

      <Box>
        <ReflectQuestionnaireContent
          thoughtsToReflect={[thoughtToReflect]}
          viewMode={viewMode}
          emptyTitle="Thought is fully reflected"
          emptyMessage="This thought has no remaining prompts."
          completeTitle="Thought is fully reflected"
          completeMessage="All reflection prompts for this thought are complete."
          showCompleteState={false}
        />
      </Box>
    </VStack>
  );
}
